import React, { useEffect, useState } from 'react';

// Redux
import { useSelector } from 'react-redux';

// Material
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';

// Helpers
import { Order } from 'helpers/tableSortLabels';

// Types
import { ApplicationState } from 'store';
import { TravelByLineInterface } from 'store/ducks/reports/types';
import { XlsHeaderInterface, XlsRowInterface } from 'components';

// Components
import { PrettiNameLine } from 'components/ReportComponents';

// Styles
import useStyles from './tableTravelByLineReport.styles';

interface TableTravelByLineReportInterface {
  setTableCSV: (arg: any) => void;
  travelByLineList?: TravelByLineInterface[];
  periodString?: string;
  setSimplePdfData?: (param: {
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }) => void;
  setXlsHeader?: (param: XlsHeaderInterface[] | undefined) => void;
  setXlsRows?: (param: Array<XlsRowInterface[]>) => void;
}

type Props = TableTravelByLineReportInterface;

const TableTravelByLineReport: React.FC<Props> = ({
  setTableCSV,
  travelByLineList,
  periodString,
  setSimplePdfData,
  setXlsHeader,
  setXlsRows,
}) => {
  const classes = useStyles();

  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState<Order>('asc');

  const [travelByLineReportList, setTravelByLineReportList] = useState<any>([]);

  const initialTravelByLineReportList = useSelector((state: ApplicationState) =>
    travelByLineList && travelByLineList.length
      ? travelByLineList
      : state.reports.travelByLineReportList,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const handleRequestSort = (property: string) => {
    setTravelByLineReportList((curr: any) =>
      curr
        .sort((a: any, b: any) => {
          if (order === 'asc') {
            if (a[property] < b[property]) {
              return -1;
            }
            if (a[property] > b[property]) {
              return 1;
            }
          } else if (order === 'desc') {
            if (a[property] > b[property]) {
              return -1;
            }
            if (a[property] < b[property]) {
              return 1;
            }
          }
          return 0;
        })
        .map((item: any) => item),
    );

    setOrderBy(property);
    if (order === 'asc') {
      setOrder('desc');
    } else if (order === 'desc') {
      setOrder('asc');
    }
  };

  // generating the table csv
  useEffect(() => {
    let csv = '';
    if (travelByLineReportList?.length > 0) {
      let header = `Linha;Quantidade;Total KM`;
      if (transportOperatorSelected.type === 1) {
        header += ';Operação\n';
      } else {
        header += '\n';
      }

      csv += header;
      csv += travelByLineReportList
        .map(
          (travel: any) =>
            `${travel.prefix} - ${travel.name};${travel.trips};${
              travel.totalKm
            };${
              transportOperatorSelected.type === 1
                ? transportOperatorsList.find(
                    (e) => e.id === travel?.transportOperatorId,
                  )?.name + '\n' || '---'
                : '\n'
            }`,
        )
        .join('');

      setTableCSV(csv);
    }
  }, [travelByLineReportList, setTableCSV]);

  useEffect(() => {
    const headerSimplePdf = ['Linha', 'Quantidade', 'Total KM'];
    const rowsSimplePdf = travelByLineReportList?.map((travel: any) => [
      `${travel.prefix} - ${travel.name}`,
      travel.trips,
      travel.totalKm?.toFixed(2),
    ]);

    const tempSimplePdfData = {
      title: 'ONBOARD',
      subtitle: 'Relatório Viagens por Linha',
      filters: periodString || '',
      headers: headerSimplePdf,
      rows: rowsSimplePdf,
    };

    if (setSimplePdfData) setSimplePdfData(tempSimplePdfData);
  }, [travelByLineReportList]);

  useEffect(() => {
    const staticXlsheader = [
      {
        header: 'Linha',
        key: 'name',
        width: 20,
      },
      { header: 'Quantidade', key: 'trips', width: 20 },
      {
        header: 'Total KM',
        key: 'totalKm',
        width: 20,
      },
    ];

    if (transportOperatorSelected.type === 1) {
      staticXlsheader.push({
        header: 'Operação',
        key: 'operator',
        width: 20,
      });
    }
    if (setXlsHeader) setXlsHeader(staticXlsheader);

    const xlsRows = travelByLineReportList?.map((travel: any) => {
      const row = [
        {
          key: 'name',
          value: `${travel.prefix} - ${travel.name}`,
          image: null,
        },
        { key: 'trips', value: `${travel.trips}`, image: null },
        {
          key: 'totalKm',
          value: `${travel.totalKm?.toFixed(2)}`,
          image: null,
        },
      ];
      if (transportOperatorSelected.type === 1) {
        row.push({
          key: 'operator',
          value: `${transportOperatorsList.find(
            (e) => e.id === travel?.transportOperatorId,
          )?.name || '---'}`,
          image: null,
        });
      }
      return row;
    }) as XlsRowInterface[][];

    if (setXlsRows) setXlsRows(xlsRows);
  }, [travelByLineReportList]);

  useEffect(() => {
    const tempTravelByLineReportList = initialTravelByLineReportList.map(
      (travel: any) => travel,
    );

    setTravelByLineReportList(tempTravelByLineReportList);
  }, [initialTravelByLineReportList]);

  return (
    <Box m={2}>
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table} aria-label='collapsible table'>
          <TableHead>
            <TableRow>
              <TableCell
                variant='head'
                align='left'
                sortDirection={orderBy === 'name' ? order : false}>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={(event) => handleRequestSort('name')}>
                  Linha
                  {orderBy === 'name' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell
                variant='head'
                align='left'
                sortDirection={orderBy === 'trips' ? order : false}>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={orderBy === 'trips' ? order : 'asc'}
                  onClick={(event) => handleRequestSort('trips')}>
                  Quantidade
                  {orderBy === 'trips' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell
                variant='head'
                align='left'
                sortDirection={orderBy === 'totalKm' ? order : false}>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={orderBy === 'totalKm' ? order : 'asc'}
                  onClick={(event) => handleRequestSort('totalKm')}>
                  Total KM
                  {orderBy === 'totalKm' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              {transportOperatorSelected.type === 1 && (
                <TableCell align='left' variant='head'>
                  Operação
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(travelByLineReportList) &&
              travelByLineReportList.map((travel: any) => (
                <TableRow key={travel.name}>
                  <TableCell>
                    <PrettiNameLine
                      line={`${travel.prefix} - ${travel.name}`}
                      color={travel?.color || '#9427EA'}
                    />
                  </TableCell>
                  <TableCell>{travel.trips}</TableCell>
                  <TableCell>{travel.totalKm?.toFixed(2)}</TableCell>
                  {transportOperatorSelected.type === 1 && (
                    <TableCell>
                      {transportOperatorsList.find(
                        (e) => e.id === travel?.transportOperatorId,
                      )?.name || '---'}
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TableTravelByLineReport;
export { TableTravelByLineReport };
